import { mdiLoading, mdiPlayOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import iziToast from 'izitoast';
import { SettingsIcon } from 'lucide-react';
import { createContext, useReducer, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import useIgnoreRenderEffect from 'src/hooks/useIgnoreRenderEffect';
import { getDocumentByIdService, upsertDocumentService } from 'src/services/DocumentService';
import { Document } from 'src/types/Document';
import { LoadingComponent } from '../../shared/LoadingComponent';
import { TabsComponent } from '../../shared/TabsComponent';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../../ui/breadcrumb';
// import { DocumentAnalysisComponent } from './DocumentAnalysisComponent';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DocumentAnalysisComponent } from './DocumentAnalysisComponent';
import { DocumentSettingsComponent } from './DocumentSettingsComponent';
import { DocumentHistoryComponent } from './DocumentHistoryComponent';

export interface DocumentContextType {
    state: {
        document: Document;
        loading: {
            document: boolean;
        };
    },
    dispatch: React.Dispatch<any>;
}

const initialState: DocumentContextType = {
    state: {
        document: new Document({}),
        loading: {
            document: false,
        },
    },
    dispatch: () => {
    },
};


// Create a slice for the context
export const { reducer, actions: DocumentActions } = createSlice({
    name: 'DocumentContextReducer',
    initialState,
    reducers: {
        setDocument: (state, action) => {
            state.state.document = action.payload;
        },
        loadingOn: (state, action: PayloadAction<keyof DocumentContextType['state']['loading']>) => {
            state.state.loading[action.payload] = true;
        },
        loadingOff: (state, action: PayloadAction<keyof DocumentContextType['state']['loading']>) => {
            state.state.loading[action.payload] = false;
        },
    },
});

// Create the context
export const DocumentContext = createContext<DocumentContextType>(initialState);

export const DocumentDetailPageComponent = () => {
    const [{ state }, dispatch] = useReducer(reducer, initialState);
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    // const { state: { session }, dispatch: dispatchApp } = useContext(AppAgentContext);


    useIgnoreRenderEffect(async () => {
        if (id) {
            dispatch(DocumentActions.loadingOn('document'));
            const response = await getDocumentByIdService(id);
            if (response.success) {
                dispatch(DocumentActions.setDocument(response.item));
                dispatch(DocumentActions.loadingOff('document'));
            } else {
                dispatch(DocumentActions.loadingOff('document'));
            }

        }
    }, [id]);

    if (state.loading.document) {
        return <LoadingComponent />;
    }

    async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const doc = new Document(state.document);
        setIsLoading(true);
        if (!id) {
            delete (doc as any).id;
        }

        console.log('state', state);
        console.log('doc', doc);
        debugger

        const resp = await upsertDocumentService(doc);
        if (resp.success) {
            setIsLoading(false);
            if (id) {
                iziToast.success({
                    message: 'Documento Actualizado',
                    position: 'topCenter',
                });
            } else {
                iziToast.success({
                    message: 'Documento Creado',
                    position: 'topCenter',
                });
                navigate(`/document_detail/${resp.item.id}`);
            }
        } else {
            setIsLoading(false);
            if (id) {
                iziToast.warning({
                    message: 'Hubo un problema al actualizar el documento',
                    position: 'topCenter',
                });
            } else {
                iziToast.warning({
                    message: 'Hubo un problema al crear el documento',
                    position: 'topCenter',
                });
            }
        }
    }

    return (
        <DocumentContext.Provider value={{ state, dispatch }}>
            <div className='mt-5 px-2'>
                <div className='flex justify-between mb-3'>
                    <div className='flex items-center'>
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink href="/">Inicio</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbLink>
                                        <Link to="/document">Documentos</Link>
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                    <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage>{id  ? state.document.name : 'Crear'}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                    <div className="bg-white p-2 flex gap-2 z-[2]">
                        <button
                            form="document_form"
                            type="submit"
                            className="btn btn-sm flex gap-2"
                            disabled={isLoading || state.document.name === '' || state.document.getSource().length < 1}
                        >
                            {id ? 'Actualizar Documento' : 'Crear Documento'}
                            {isLoading && <Icon className="mr-1" size={1} path={mdiLoading} spin />}
                        </button>
                    </div>
                </div>
                <div className="w-full">
                    <form id="document_form" onSubmit={onSubmit}>
                        <TabsComponent tabsPosition='left' paramsUse tabs={
                            [
                                {
                                    label: 'Documento',
                                    component: <DocumentSettingsComponent />,
                                    disabled: false,
                                    icon: <SettingsIcon />,
                                    visible: true,
                                },
                                {
                                    label: 'Analizar Documento',
                                    component: <DocumentAnalysisComponent />,
                                    disabled: false,
                                    icon: <Icon path={mdiPlayOutline} className='w-[24px]' />,
                                    visible: true,
                                },
                                {
                                    label: 'Historial',
                                    component: <DocumentHistoryComponent />,
                                    disabled: false,
                                    icon: <FontAwesomeIcon icon={faClockRotateLeft} />,
                                    visible: !location.pathname.includes('create'),
                                },
                            ]
                        } refreshContent={false} />
                    </form>
                </div>
            </div>
        </DocumentContext.Provider>
    );
};
